import api from "./api-utils";

/**
 * Tenant row from GetAvailableTenants (C# API, PascalCase).
 * @typedef {Object} ApiTenant
 * @property {number} TenantId
 * @property {string} DisplayName
 * @property {boolean} EnableOneView
 * @property {string} [LogoUrl]
 * @property {Object} [Logo]
 * @property {Object|Object[]} [TenantLogos]
 * @property {string} [DataBase64]
 * @property {string} [MimeType]
 */

/**
 * @typedef {Object} NormalizedTenant
 * @property {number} tenantId
 * @property {string} displayName
 * @property {boolean} oneViewEnabled
 * @property {string|null} logoUrl
 * @property {string|null} [logoBase64]
 * @property {string|null} [logoMimeType]
 */

/**
 * @param {ApiTenant} raw
 * @returns {NormalizedTenant}
 */
export function normalizeTenant(raw) {
  let logoBase64 = null;
  let logoMimeType = null;

  const applyLogoDto = (dto) => {
    if (!dto) return;
    if (!logoBase64 && dto.DataBase64) logoBase64 = dto.DataBase64;
    if (!logoMimeType && dto.MimeType) logoMimeType = dto.MimeType;
  };

  applyLogoDto(raw.Logo);
  const tenantLogos = raw.TenantLogos;
  if (Array.isArray(tenantLogos)) {
    tenantLogos.forEach(applyLogoDto);
  } else {
    applyLogoDto(tenantLogos);
  }
  if (!logoBase64 && raw.DataBase64) logoBase64 = raw.DataBase64;
  if (!logoMimeType && raw.MimeType) logoMimeType = raw.MimeType;

  return {
    tenantId: raw.TenantId,
    displayName: raw.DisplayName || `Tenant ${raw.TenantId}`,
    oneViewEnabled: raw.EnableOneView,
    logoUrl: raw.LogoUrl || null,
    logoBase64,
    logoMimeType,
  };
}

/**
 * @typedef {Object} AvailableTenantsSession
 * @property {NormalizedTenant[]} availableTenants
 * @property {number|null} currentTenantId
 * @property {NormalizedTenant|null} currentTenant
 */

/**
 * @typedef {Object} GetAvailableTenantsResponse
 * @property {ApiTenant[]} availableTenants
 * @property {number} [currentTenantId]
 */

/**
 * Fetch and normalize session tenant list from Detechtion account API.
 * @returns {Promise<AvailableTenantsSession>}
 */
export async function getAvailableTenantsSession() {
  const response = await api.get("/DetechtionAccount/GetAvailableTenants", {
    params: { oneView: true },
  });
  const data = /** @type {GetAvailableTenantsResponse} */ (response.data);
  const list = (data.availableTenants ?? []).map(normalizeTenant);
  const rawCurrent = data.currentTenantId;
  const currentTenantId =
    rawCurrent != null && rawCurrent !== "" ? Number(rawCurrent) : null;
  const currentTenant =
    currentTenantId != null && Number.isFinite(currentTenantId)
      ? (list.find((t) => t.tenantId === currentTenantId) ?? null)
      : null;

  return {
    availableTenants: list,
    currentTenantId,
    currentTenant,
  };
}

export const getSessionTenant = async () => {
  const session = await getAvailableTenantsSession();
  return session.currentTenant?.displayName ?? null;
};

/**
 * Switch active session tenant (existing backend action).
 * @param {number} desiredTenantId
 * @returns {Promise<unknown>}
 */
export async function changeSessionTenant(desiredTenantId) {
  const response = await api.get("/DetechtionAccount/ChangeSessionTenant", {
    params: { desiredTenantId },
  });
  return response.data;
}

export const logOut = (callback) => {
  const requestBody = {};
  const response = api.post("/DetechtionAccount/LogOut", requestBody);
  response.then((_) => {
    callback?.();
  });
};

export const getAuthorizationStatus = (callback) => {
  const requestBody = {};
  const response = api.post("/DetechtionAccount/GetAuthStatus", requestBody);
  response.then((res) => {
    callback?.(res.data);
  });
};

/**
 * Fetches tenants for which the current user can manage tag groups.
 * @returns {Promise<Array<{TenantId:number, DisplayName:string}>>} Tenant list
 */
export const getManageTagGroupTenants = async () => {
  const response = await api.post("/Tenant/GetTenantListFor", {
    OneviewAdminFunctions: true,
  });

  if (Array.isArray(response?.data.tenantList)) {
    return response.data.tenantList;
  }

  return [];
};
